import { useEffect, useRef, useState } from 'react'
import { Html5Qrcode } from 'html5-qrcode'

/**
 * Modal con scanner de QR. Usa la cámara trasera del celu.
 *
 * Props:
 * - onDetect(text)   → callback con el texto del QR detectado
 * - onClose()        → cierra el modal sin escanear
 *
 * Detecta cualquier QR; el caller decide qué hacer con el texto.
 */
export default function QRScanner({ onDetect, onClose }) {
  const containerId = 'qr-scanner-region'
  const scannerRef = useRef(null)
  const [err, setErr] = useState(null)
  const [stage, setStage] = useState('starting')  // starting | scanning | error

  useEffect(() => {
    let mounted = true
    const scanner = new Html5Qrcode(containerId, /* verbose */ false)
    scannerRef.current = scanner

    const config = {
      fps: 10,
      qrbox: (vw, vh) => {
        const min = Math.min(vw, vh)
        const size = Math.floor(min * 0.7)
        return { width: size, height: size }
      },
      aspectRatio: 1,
    }

    scanner.start(
      { facingMode: 'environment' },  // cámara trasera
      config,
      (decodedText) => {
        if (!mounted) return
        // Detenemos antes de notificar para evitar lecturas duplicadas
        scanner.stop().catch(() => {}).finally(() => {
          mounted && onDetect(decodedText)
        })
      },
      // onError por frame: no spam-eamos errores de "no se ve QR todavía"
      () => {}
    ).then(() => {
      if (mounted) setStage('scanning')
    }).catch((e) => {
      console.error('QR start:', e)
      if (mounted) {
        const msg = String(e?.message || e || '')
        if (/permission|denied|notallowed/i.test(msg)) {
          setErr('Hay que dar permiso a la cámara. Tocá el ícono de candado al lado de la URL.')
        } else if (/notfound|nodevice/i.test(msg)) {
          setErr('No encuentro cámara en este dispositivo.')
        } else {
          setErr('No pude abrir la cámara: ' + msg)
        }
        setStage('error')
      }
    })

    return () => {
      mounted = false
      try {
        scanner.stop().catch(() => {})
      } catch (_) {}
    }
  }, [onDetect])

  return (
    <div className="qrs-bg" onClick={onClose}>
      <div className="qrs-modal" onClick={e => e.stopPropagation()}>
        <div className="qrs-head">
          <div className="qrs-title">📷 Escanear QR</div>
          <button onClick={onClose} className="qrs-close">✕</button>
        </div>

        <div className="qrs-body">
          <div id={containerId} className="qrs-camera"></div>
          {stage === 'starting' && <div className="qrs-overlay">Abriendo cámara…</div>}
          {stage === 'scanning' && (
            <>
              <div className="qrs-frame"></div>
              <div className="qrs-hint">Apuntá al QR de la etiqueta</div>
            </>
          )}
          {stage === 'error' && err && (
            <div className="qrs-error">
              ⚠️ {err}
              <button onClick={onClose} className="qrs-error-btn">Cerrar</button>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .qrs-bg {
          position: fixed; inset: 0; z-index: 2000;
          background: rgba(0,0,0,.85);
          display: flex; align-items: center; justify-content: center;
          padding: 16px;
        }
        .qrs-modal {
          background: #111; color: #fff;
          width: 100%; max-width: 480px;
          border-radius: 16px; overflow: hidden;
          display: flex; flex-direction: column;
        }
        .qrs-head {
          display: flex; justify-content: space-between; align-items: center;
          padding: 14px 16px;
          background: rgba(255,255,255,.05);
          border-bottom: 1px solid rgba(255,255,255,.1);
        }
        .qrs-title { font-size: 15px; font-weight: 800; letter-spacing: .02em }
        .qrs-close {
          background: rgba(255,255,255,.15); color: #fff;
          border: 0; border-radius: 50%;
          width: 32px; height: 32px; font-size: 16px;
          cursor: pointer; line-height: 1;
        }
        .qrs-body {
          position: relative;
          aspect-ratio: 1;
          background: #000;
        }
        .qrs-camera, .qrs-camera video {
          width: 100% !important;
          height: 100% !important;
          object-fit: cover !important;
        }
        .qrs-overlay {
          position: absolute; inset: 0;
          display: flex; align-items: center; justify-content: center;
          color: #fff; font-size: 14px; font-weight: 600;
          background: rgba(0,0,0,.6);
        }
        .qrs-frame {
          position: absolute;
          top: 15%; left: 15%; right: 15%; bottom: 15%;
          border: 3px solid #00A8B5;
          border-radius: 16px;
          box-shadow: 0 0 0 9999px rgba(0,0,0,.4);
          pointer-events: none;
        }
        .qrs-hint {
          position: absolute; bottom: 16px; left: 0; right: 0;
          text-align: center;
          font-size: 13px; font-weight: 700;
          color: #fff; text-shadow: 0 2px 4px rgba(0,0,0,.6);
          pointer-events: none;
        }
        .qrs-error {
          position: absolute; inset: 0;
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          padding: 24px; gap: 16px; text-align: center;
          background: rgba(0,0,0,.85); color: #fff;
        }
        .qrs-error-btn {
          background: #00A8B5; color: #fff; border: 0;
          padding: 10px 20px; border-radius: 8px;
          font-weight: 700; cursor: pointer;
        }
      `}</style>
    </div>
  )
}
